import React from 'react';
import { Link } from 'react-router-dom';
import useScrollAnimation from '../../Hooks/useScrollAnimation';
import './PrivacyPolicy.css';

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
  </svg>
);

const tocItems = [
  { id: 'tos-01', label: 'Agreement to Terms' },
  { id: 'tos-02', label: 'Membership Eligibility' },
  { id: 'tos-03', label: 'Fees & Payment' },
  { id: 'tos-04', label: 'Financial Protection' },
  { id: 'tos-05', label: 'Member Conduct' },
  { id: 'tos-06', label: 'Suspension & Termination' },
  { id: 'tos-07', label: 'Intellectual Property' },
  { id: 'tos-08', label: 'Disclaimers & Liability' },
  { id: 'tos-09', label: 'Dispute Resolution' },
  { id: 'tos-10', label: 'Amendments' },
];

const TermsOfService = () => {
  useScrollAnimation();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="container">
          <div className="legal-hero-inner">
            <div className="legal-hero-tag">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              Terms of Service
            </div>
            <h1 className="legal-hero-title">Clear Terms, No Surprises</h1>
            <p className="legal-hero-subtitle">
              These terms outline the rules and expectations for using EFFN's platform and membership services. Please read them before proceeding.
            </p>
            <div className="legal-hero-pills">
              <span className="legal-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Effective: January 2025
              </span>
              <span className="legal-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                Last Updated: January 2025
              </span>
              <span className="legal-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/>
                </svg>
                Applies Globally
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="legal-layout">
        <aside className="legal-toc">
          <p className="legal-toc-title">On This Page</p>
          <ul className="legal-toc-list">
            {tocItems.map((item, i) => (
              <li key={item.id}>
                <a href={"#" + item.id} onClick={(e) => { e.preventDefault(); scrollTo(item.id); }}>
                  <span className="toc-num">{String(i + 1).padStart(2, '0')}</span>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <main className="legal-main">
          <div className="legal-intro-card">
            <p>
              These Terms of Service form a binding agreement between you and Elite Freight Forwarders Network ("EFFN", "we", "us", "our"). They govern your access to and use of our website, membership services, and any related communications. By visiting our site, submitting an application, or becoming a member, you confirm that you have read, understood, and agreed to these Terms in full.
            </p>
          </div>

          <div className="legal-section scroll-reveal" id="tos-01">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 01</span>
                <h2 className="legal-section-title">Agreement to Terms</h2>
              </div>
            </div>
            <p>By accessing our website or using any of our services, you agree to be legally bound by these Terms and our Privacy Policy. These Terms apply to all users, including visitors, applicants, and active members.</p>
            <p>If you are acting on behalf of a company or organisation, you represent that you have the authority to bind that entity to these Terms. If you do not agree with any part of these Terms, you must discontinue use of our website and services immediately.</p>
          </div>

          <div className="legal-section scroll-reveal" id="tos-02">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 02</span>
                <h2 className="legal-section-title">Membership Eligibility</h2>
              </div>
            </div>
            <p>EFFN membership is available to established freight forwarding and logistics companies that meet our professional standards. By submitting an application, you confirm the following:</p>
            <ul className="legal-list">
              {[
                'All information provided in your application is truthful, accurate, and current',
                'Your company is a legally registered entity in its country of operation',
                'You hold the authority to enter into this agreement on behalf of your organisation',
                'Your business operates in full compliance with applicable laws and industry regulations',
                'You commit to upholding the professional standards required of all EFFN members',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <div className="legal-highlight">
              <InfoIcon />
              <span>EFFN reserves the right to approve or decline any membership application at its sole discretion. We are not obligated to provide a reason for any decision made during the review process.</span>
            </div>
          </div>

          <div className="legal-section scroll-reveal" id="tos-03">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 03</span>
                <h2 className="legal-section-title">Fees & Payment</h2>
              </div>
            </div>
            <p>Membership fees are structured as follows. All amounts are in United States Dollars (USD) and are subject to change with reasonable advance notice:</p>
            <table className="legal-fee-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Total Fee</th>
                  <th>Effective Rate</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Annual</td>
                  <td>USD 1,200</td>
                  <td>USD 1,200 / year</td>
                  <td>1 Year</td>
                </tr>
                <tr>
                  <td>2-Year <span className="fee-badge">Popular</span></td>
                  <td>USD 2,000</td>
                  <td>USD 1,000 / year</td>
                  <td>2 Years</td>
                </tr>
                <tr>
                  <td>3-Year <span className="fee-badge">Best Value</span></td>
                  <td>USD 2,850</td>
                  <td>USD 950 / year</td>
                  <td>3 Years</td>
                </tr>
              </tbody>
            </table>
            <div className="legal-highlight" style={{marginTop: '20px'}}>
              <InfoIcon />
              <span>Membership fees are payable in full before benefits are activated. All fees are non-refundable once membership has been confirmed, except where required by applicable law.</span>
            </div>
          </div>

          <div className="legal-section scroll-reveal" id="tos-04">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 04</span>
                <h2 className="legal-section-title">Financial Protection Program</h2>
              </div>
            </div>
            <p>EFFN provides a financial protection program to reduce risk when transacting with fellow network members. Coverage levels are tied to your membership plan:</p>
            <table className="legal-fee-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Max Coverage</th>
                  <th>Scope</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Annual</td>
                  <td>USD 7,000</td>
                  <td>Member-to-member transactions</td>
                </tr>
                <tr>
                  <td>2-Year</td>
                  <td>USD 10,000</td>
                  <td>Member-to-member transactions</td>
                </tr>
                <tr>
                  <td>3-Year</td>
                  <td>USD 15,000</td>
                  <td>Member-to-member transactions</td>
                </tr>
              </tbody>
            </table>
            <p style={{marginTop: '16px'}}>The following conditions apply to all protection claims:</p>
            <ul className="legal-list">
              {[
                'Coverage applies only to freight charges and directly related logistics transactions between active EFFN members',
                'Claims must be submitted within the timeframe specified in your membership agreement',
                'Coverage does not extend to disputes involving service quality, cargo damage, or delivery performance',
                'EFFN will investigate all claims and its determination on eligibility is final',
                'Fraudulent or exaggerated claims will result in immediate membership termination',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
          </div>

          <div className="legal-section scroll-reveal" id="tos-05">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 05</span>
                <h2 className="legal-section-title">Member Conduct</h2>
              </div>
            </div>
            <p>All EFFN members are expected to conduct themselves with the highest level of professionalism. As a member, you agree to:</p>
            <ul className="legal-list">
              {[
                'Act with integrity and honesty in all dealings within the network',
                'Settle all financial obligations to fellow members promptly and in full',
                'Keep your company profile, contact details, and credentials up to date',
                'Notify EFFN promptly of any significant changes to your business operations',
                'Treat all network communications and member information as confidential',
                'Refrain from any conduct that could harm the reputation or interests of EFFN or its members',
                'Engage constructively in network events, forums, and communications',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <div className="legal-highlight">
              <InfoIcon />
              <span>Violations of these conduct standards may result in suspension or permanent removal from the network, at EFFN's sole discretion.</span>
            </div>
          </div>

          <div className="legal-section scroll-reveal" id="tos-06">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 06</span>
                <h2 className="legal-section-title">Suspension & Termination</h2>
              </div>
            </div>
            <p>EFFN may suspend or permanently terminate a membership under any of the following circumstances:</p>
            <ul className="legal-list">
              {[
                'Non-payment of membership fees by the due date',
                'Material breach of any provision within these Terms',
                'Conduct that damages the reputation, interests, or operations of EFFN or its members',
                'Submission of false, misleading, or incomplete information during the application process',
                'Insolvency, bankruptcy, or permanent cessation of business activities',
                'Failure to maintain the professional standards required of network members',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <p>Upon termination, all membership benefits cease immediately. No refund of fees will be issued for early termination unless required by applicable law. Members may appeal a suspension decision by contacting EFFN in writing within 14 days.</p>
          </div>

          <div className="legal-section scroll-reveal" id="tos-07">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <path d="M3 9h18M9 21V9"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 07</span>
                <h2 className="legal-section-title">Intellectual Property</h2>
              </div>
            </div>
            <p>All content published on the EFFN website — including written text, graphics, logos, icons, images, and software — is the exclusive property of Elite Freight Forwarders Network and is protected under applicable intellectual property laws.</p>
            <p>You may not copy, reproduce, distribute, modify, publicly display, or create derivative works from any content on this website without obtaining prior written permission from EFFN. The EFFN name, logo, and associated marks are proprietary identifiers of our organisation and may not be used without authorisation.</p>
            <div className="legal-highlight">
              <InfoIcon />
              <span>Permitted use is limited to personal, non-commercial reference only. Any other use requires explicit written consent from EFFN.</span>
            </div>
          </div>

          <div className="legal-section scroll-reveal" id="tos-08">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 08</span>
                <h2 className="legal-section-title">Disclaimers & Liability</h2>
              </div>
            </div>
            <p>Our website and services are provided on an "as available" basis. While we make every effort to ensure accuracy and reliability, EFFN makes no warranties — express or implied — regarding the completeness, accuracy, or fitness for purpose of any content or service.</p>
            <p>To the maximum extent permitted by law, EFFN shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of our website or services, including but not limited to loss of revenue, data, or business opportunities.</p>
            <div className="legal-highlight">
              <InfoIcon />
              <span>EFFN's total liability for any claim under these Terms shall not exceed the total membership fees paid by you in the twelve months immediately preceding the claim.</span>
            </div>
          </div>

          <div className="legal-section scroll-reveal" id="tos-09">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 09</span>
                <h2 className="legal-section-title">Dispute Resolution</h2>
              </div>
            </div>
            <p>In the event of a dispute arising from or related to these Terms or your use of EFFN services, both parties agree to first attempt resolution through direct, good-faith negotiation within 30 days of the dispute arising.</p>
            <p>If a resolution cannot be reached through negotiation, the dispute shall be referred to binding arbitration under internationally recognised arbitration rules. The arbitration shall be conducted in English, and the arbitrator's decision shall be final and binding on both parties.</p>
            <p>These Terms are governed by general principles of international commercial law. Nothing in this clause limits either party's right to seek urgent injunctive or equitable relief from a competent court.</p>
          </div>

          <div className="legal-section scroll-reveal" id="tos-10">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 10</span>
                <h2 className="legal-section-title">Amendments</h2>
              </div>
            </div>
            <p>EFFN may update or revise these Terms at any time. When material changes are made, we will update the effective date shown at the top of this page. Where appropriate, we will also notify active members via the email address on file.</p>
            <p>Your continued use of our website or services following any update constitutes your acceptance of the revised Terms. If you do not agree with the updated Terms, you should discontinue use of our services and notify us in writing.</p>
          </div>

          <div className="legal-cta-box">
            <div className="legal-cta-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <h3>Need Clarification on These Terms?</h3>
            <p>If anything in this document is unclear or you have specific questions about your membership, our team is ready to help.</p>
            <div className="legal-cta-buttons">
              <Link to="/contact" className="btn-legal-primary">
                Contact Us
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link to="/privacy" className="btn-legal-secondary">
                View Privacy Policy
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TermsOfService;
